import React, { useState, useEffect } from 'react';
import { X, BookOpen, Loader2, Volume2, BookmarkPlus, Check, Copy } from 'lucide-react';
import { apiClient } from '../services/api';
import { logCardReview } from '../utils/reviewUtils';
import { trackAIGenerated } from '../utils/analytics';
import { speak, stopSpeech } from '../utils/speechUtils';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface SynonymsResult {
  word: string;
  items: { synonym: string; example: string }[];
}

interface GenerateSynonymsModalProps {
  cardId: number;
  cardName: string;
  onClose: () => void;
  onAddSynonym?: (synonym: string) => Promise<void>;
}

export const GenerateSynonymsModal: React.FC<GenerateSynonymsModalProps> = ({ cardId, cardName, onClose, onAddSynonym }) => {
  useBodyScrollLock();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<SynonymsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [addedIndices, setAddedIndices] = useState<Set<number>>(new Set());
  const [addingIndex, setAddingIndex] = useState<number | null>(null);

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const handleSpeak = (text: string, index: number) => {
    if (speakingIndex === index) {
      stopSpeech();
      setSpeakingIndex(null);
      return;
    }
    speak(text, { onEnd: () => setSpeakingIndex(null), onError: () => setSpeakingIndex(null) });
    setSpeakingIndex(index);
  };

  useEffect(() => {
    return () => { stopSpeech(); };
  }, []);

  const handleAdd = async (synonym: string, index: number) => {
    if (!onAddSynonym || addedIndices.has(index)) return;
    setAddingIndex(index);
    try {
      await onAddSynonym(synonym);
      setAddedIndices(prev => new Set(prev).add(index));
    } finally {
      setAddingIndex(null);
    }
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.generateSynonyms(cardId, { prompt: prompt.trim() || undefined });
      setResult(data);
      setSpeakingIndex(null);
      logCardReview(cardId);
      trackAIGenerated('synonyms', cardId);
    } catch {
      setError('Could not generate synonyms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Synonyms</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">&quot;{cardName}&quot;</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          <div>
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !isLoading) handleGenerate(); }}
              placeholder='e.g. "as a verb", "in informal speech"'
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/80 focus:border-blue-300 bg-white text-sm"
              disabled={isLoading}
            />
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={isLoading}
            className="w-full px-4 py-3 text-[15px] font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <BookOpen className="h-5 w-5" />
                {result ? 'Generate again' : 'Generate'}
              </>
            )}
          </button>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
          )}

          {result && (
            <div className="space-y-3">
              {result.items.map((item, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm font-semibold text-gray-900">{item.synonym}</p>
                  <div className="flex items-start justify-between gap-2 mt-1">
                    <p className="text-sm text-gray-500 italic flex-1">&quot;{item.example}&quot;</p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleSpeak(item.example, i)}
                        className={`p-1 rounded-full transition-colors ${speakingIndex === i ? 'text-blue-600 bg-blue-50' : 'text-gray-300 hover:text-blue-500 hover:bg-blue-50'}`}
                        title="Read aloud"
                      >
                        <Volume2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopy(item.synonym, i)}
                        className="p-1 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                        title="Copy"
                      >
                        {copiedIndex === i ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                      {onAddSynonym && (
                        <button
                          type="button"
                          onClick={() => handleAdd(item.synonym, i)}
                          disabled={addedIndices.has(i) || addingIndex === i}
                          className="p-1 text-gray-300 hover:text-amber-600 hover:bg-amber-50 rounded-full transition-colors disabled:cursor-not-allowed"
                          title={addedIndices.has(i) ? 'Added to card' : 'Add to card'}
                        >
                          {addingIndex === i ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : addedIndices.has(i) ? (
                            <Check className="h-3.5 w-3.5 text-amber-500" />
                          ) : (
                            <BookmarkPlus className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
